import { REPORT_TYPES } from '../consts/reportTypes';
import { designPath } from './design';
import { dashboardPath } from './projectPath';

// Mirrors WORKFLOW_STAGE_KEYS in src/hooks/useWorkflowStages.ts. Duplicated
// here (rather than imported) so this module stays JS-only — useWorkflowStages
// is .ts and pulling it in would couple every consumer to the hook's TS types.
export const WORKFLOW_STAGE_KEYS = {
  DASHBOARD: 'dashboard',
  AIFS: 'aifs',
  JURISDICTIONS: 'jurisdictions',
  SOURCE_FILES: 'source_files',
  EXTRACTION: 'extraction',
  TRANSFORMATION: 'transformation',
  LOOKUP_TABLES: 'lookup_tables',
  OUTPUT_DATA: 'output_data',
  REPORT: 'report',
  APPROVAL: 'approval',
  SUBMISSION: 'submission'
};

export const STAGE_ORDER = [
  WORKFLOW_STAGE_KEYS.DASHBOARD,
  WORKFLOW_STAGE_KEYS.AIFS,
  WORKFLOW_STAGE_KEYS.JURISDICTIONS,
  WORKFLOW_STAGE_KEYS.SOURCE_FILES,
  WORKFLOW_STAGE_KEYS.EXTRACTION,
  WORKFLOW_STAGE_KEYS.LOOKUP_TABLES,
  WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
  WORKFLOW_STAGE_KEYS.REPORT,
  WORKFLOW_STAGE_KEYS.APPROVAL,
  WORKFLOW_STAGE_KEYS.SUBMISSION
];

const REPORT_PATH_PREFIX = /^\/(report-v2|report\/(fca|esma|pf))\//;

/**
 * Map a React Router location to the workflow stage the user is currently on.
 * Returns null when the page is not a per-period stepper stage (e.g. the
 * Quarters dashboard, project list, integrations page).
 */
export const getStageFromLocation = (pathname, search) => {
  const params = new URLSearchParams(search || '');

  if (REPORT_PATH_PREFIX.test(pathname)) {
    if (params.get('openSubmitDrawer') === 'true') return WORKFLOW_STAGE_KEYS.SUBMISSION;
    if (params.get('openApprovalDrawer') === 'true') return WORKFLOW_STAGE_KEYS.APPROVAL;
    return WORKFLOW_STAGE_KEYS.REPORT;
  }

  if (pathname.startsWith('/output-tables/')) return WORKFLOW_STAGE_KEYS.OUTPUT_DATA;

  if (pathname.startsWith('/data-extraction/')) {
    const tab = params.get('tab');
    if (tab === 'lookup_tables') return WORKFLOW_STAGE_KEYS.LOOKUP_TABLES;
    if (tab === 'transformation') return WORKFLOW_STAGE_KEYS.TRANSFORMATION;
    return WORKFLOW_STAGE_KEYS.EXTRACTION;
  }

  if (pathname.endsWith('/upload-files')) return WORKFLOW_STAGE_KEYS.SOURCE_FILES;
  if (pathname.endsWith('/add-aifs')) return WORKFLOW_STAGE_KEYS.AIFS;

  // Jurisdictions has two URL shapes: the dedicated automation route
  // (/project/quaters/:wf/:p/jurisdictions) and the scratch query form
  // (/project/:wf/jurisdictions?periodId=:p).
  if (pathname.endsWith('/jurisdictions') || pathname.includes('/jurisdictions')) {
    return WORKFLOW_STAGE_KEYS.JURISDICTIONS;
  }

  // Legacy /project/:wf/funds URL — AIFs used to live there; treat as AIFs
  // so a period switch from a legacy bookmark still lands on AddAIFs.
  if (/\/project\/[^/]+\/funds$/.test(pathname)) return WORKFLOW_STAGE_KEYS.AIFS;

  return null;
};

/**
 * Same gates the stepper's `useWorkflowStages` applies when it sets
 * `path = '#'`. A stage is "reachable" for `destPeriod` if the workflow has
 * progressed far enough that landing on the page would be useful.
 *
 * Stages that don't progress-gate (AIFs, Source Files, the extraction
 * family) are always reachable — the destination page renders an empty/
 * placeholder state when there's nothing to show.
 */
export const isStageReachable = (stage, destPeriod, project) => {
  if (!stage || !destPeriod) return false;

  const reportType = project?.report_type;
  const reportProgress = destPeriod?.report_progress_status;
  const approveProgress = destPeriod?.approve_progress_status;
  const submitProgress = destPeriod?.submit_progress_status;

  // Period-switch reachability for the Report stage: as long as the workflow
  // has actually started generating (`In progress` or `Completed`), let the
  // user navigate. The Report page handles its own "still generating…"
  // state. Only `Not started` (or missing) bounces the user back to the
  // closest earlier stage.
  const isReportReachable = !!reportProgress && reportProgress !== 'Not started';

  switch (stage) {
    case WORKFLOW_STAGE_KEYS.DASHBOARD:
    case WORKFLOW_STAGE_KEYS.AIFS:
    case WORKFLOW_STAGE_KEYS.SOURCE_FILES:
    case WORKFLOW_STAGE_KEYS.EXTRACTION:
    case WORKFLOW_STAGE_KEYS.TRANSFORMATION:
    case WORKFLOW_STAGE_KEYS.LOOKUP_TABLES:
    case WORKFLOW_STAGE_KEYS.OUTPUT_DATA:
      return true;
    case WORKFLOW_STAGE_KEYS.JURISDICTIONS:
      return reportType !== REPORT_TYPES.CUSTOM;
    case WORKFLOW_STAGE_KEYS.REPORT:
      return isReportReachable;
    case WORKFLOW_STAGE_KEYS.APPROVAL:
      return isReportReachable || approveProgress === 'Completed';
    case WORKFLOW_STAGE_KEYS.SUBMISSION:
      return submitProgress === 'Completed';
    default:
      return false;
  }
};

/**
 * Filter STAGE_ORDER for the destination period's report type. Mirrors the
 * Conversion / Custom skips in useWorkflowStages.
 *
 * `destPeriod` is optional — when its `all_national_codes_nil_return` flag is
 * set (every included fund's national code in the period is Nil Return),
 * Source Files/Extraction/Transformation/Lookup Tables/Output Data are
 * dropped, mirroring the same filter in useWorkflowStages. This shortcut is
 * Annex IV-only; all other report types retain their own stage sequence.
 */
const orderForReportType = (reportType, destPeriod) => {
  if (reportType === REPORT_TYPES.CONVERSION) {
    // Conversion has its own visual sequence — Source Files comes BEFORE
    // AIFs / Jurisdictions. Mirrors `useWorkflowStages` exactly so the
    // stepper and the period switcher walk the same order.
    return [
      WORKFLOW_STAGE_KEYS.SOURCE_FILES,
      WORKFLOW_STAGE_KEYS.AIFS,
      WORKFLOW_STAGE_KEYS.JURISDICTIONS,
      WORKFLOW_STAGE_KEYS.REPORT,
      WORKFLOW_STAGE_KEYS.APPROVAL,
      WORKFLOW_STAGE_KEYS.SUBMISSION
    ];
  }
  let order = STAGE_ORDER.filter(k => k !== WORKFLOW_STAGE_KEYS.DASHBOARD);
  if (reportType === REPORT_TYPES.CUSTOM) {
    order = order.filter(k => k !== WORKFLOW_STAGE_KEYS.JURISDICTIONS);
  }
  if (reportType === REPORT_TYPES.ANNEX_IV && destPeriod?.all_national_codes_nil_return) {
    const nilReturnSkippedKeys = new Set([
      WORKFLOW_STAGE_KEYS.SOURCE_FILES,
      WORKFLOW_STAGE_KEYS.EXTRACTION,
      WORKFLOW_STAGE_KEYS.TRANSFORMATION,
      WORKFLOW_STAGE_KEYS.LOOKUP_TABLES,
      WORKFLOW_STAGE_KEYS.OUTPUT_DATA
    ]);
    order = order.filter(k => !nilReturnSkippedKeys.has(k));
  }
  return order;
};

/**
 * Compute the FCA vs ESMA report URL. Form PF + Conversion both use
 * /report/.../{state} so the helper falls back to PF when the report type
 * indicates so. Returns null when a member state can't be derived (forces
 * the caller to keep walking the stage order).
 */
const buildReportPath = (workflowId, periodId, project, jurisdictions) => {
  if (project?.report_type === REPORT_TYPES.FORM_PF) {
    return `/report/pf/us/${workflowId}/${periodId}`;
  }
  const state = jurisdictions?.[0]?.reporting_member_state;
  if (!state) return null;
  const reportKind = state === 'UK' ? 'fca' : 'esma';
  return `/report/${reportKind}/${String(state).toLowerCase()}/${workflowId}/${periodId}`;
};

/**
 * Build the canonical URL for a given stage + period. Mirrors `buildPath` in
 * useWorkflowStages.ts but exposed as a standalone helper. Returns null when
 * the URL can't be built (e.g. REPORT without jurisdictions for a non-Form-PF
 * workflow) — caller walks the stage order to the next reachable stage.
 */
export const buildStagePath = (stage, workflowId, periodId, period, project, jurisdictions) => {
  if (!workflowId || !periodId) return null;
  switch (stage) {
    case WORKFLOW_STAGE_KEYS.DASHBOARD:
      return dashboardPath(workflowId, project);
    case WORKFLOW_STAGE_KEYS.AIFS:
      return `/project/quaters/${workflowId}/${periodId}/add-aifs`;
    case WORKFLOW_STAGE_KEYS.JURISDICTIONS:
      // Both scratch and automation flows use the /quaters/... route — the
      // Jurisdictions page reads `useParams()` + `useSearchParams()` to
      // resolve workflowId/periodId either way. Using the unified URL keeps
      // period-switching correct even when the destination period's
      // `template_type` isn't populated in the workflow's periods array (it
      // sometimes comes through as undefined, which previously flipped this
      // branch to the scratch URL for an automation destination).
      return `/project/quaters/${workflowId}/${periodId}/jurisdictions`;
    case WORKFLOW_STAGE_KEYS.SOURCE_FILES:
      return `/project/quaters/${workflowId}/${periodId}/upload-files`;
    case WORKFLOW_STAGE_KEYS.EXTRACTION:
      return `/data-extraction/${workflowId}/${periodId}?tab=extraction`;
    case WORKFLOW_STAGE_KEYS.TRANSFORMATION:
      return `/data-extraction/${workflowId}/${periodId}?tab=transformation`;
    case WORKFLOW_STAGE_KEYS.LOOKUP_TABLES:
      return `/data-extraction/${workflowId}/${periodId}?tab=lookup_tables`;
    case WORKFLOW_STAGE_KEYS.OUTPUT_DATA:
      return `/output-tables/${workflowId}/${periodId}`;
    case WORKFLOW_STAGE_KEYS.REPORT:
      return designPath(
        `/report-v2/${workflowId}/${periodId}`,
        buildReportPath(workflowId, periodId, project, jurisdictions)
      );
    case WORKFLOW_STAGE_KEYS.APPROVAL: {
      const base = designPath(
        `/report-v2/${workflowId}/${periodId}`,
        buildReportPath(workflowId, periodId, project, jurisdictions)
      );
      if (!base) return null;
      return base.includes('?')
        ? `${base}&openApprovalDrawer=true`
        : `${base}?openApprovalDrawer=true`;
    }
    case WORKFLOW_STAGE_KEYS.SUBMISSION: {
      const base = buildReportPath(workflowId, periodId, project, jurisdictions);
      if (!base) return null;
      return base.includes('?') ? `${base}&openSubmitDrawer=true` : `${base}?openSubmitDrawer=true`;
    }
    default:
      return null;
  }
};

/**
 * Pick the best destination URL when the user switches to `newPeriodId`
 * while sitting on a stage page (`requestedStage`).
 *
 * Strategy:
 *   1. If the same stage is reachable for the destination period, navigate
 *      there (best UX — user keeps reading the same kind of content).
 *   2. Otherwise walk STAGE_ORDER backward until we find a reachable stage
 *      that the period type supports. Output Data / Extraction / AIFs are
 *      always reachable, so the walk always terminates.
 *
 * `destJurisdictions` is only required for REPORT / APPROVAL / SUBMISSION on
 * Annex IV / Conversion (FCA vs ESMA depends on the first jurisdiction's
 * reporting_member_state). Pass undefined for other stages; the helper
 * falls through to an earlier stage if jurisdictions are missing.
 */
export const pickPeriodSwitchDestination = ({
  requestedStage,
  workflowId,
  newPeriodId,
  destPeriod,
  project,
  destJurisdictions
}) => {
  const fallback = `/project/quaters/${workflowId}/${newPeriodId}/add-aifs`;
  if (!workflowId || !newPeriodId || !destPeriod) return fallback;

  if (!requestedStage) return fallback;

  const canonical = STAGE_ORDER.filter(k => k !== WORKFLOW_STAGE_KEYS.DASHBOARD);
  if (canonical.indexOf(requestedStage) < 0) return fallback;

  const order = orderForReportType(project?.report_type, destPeriod);
  // Anchor "where the user was" directly in the filtered order. The previous
  // canonical-monotonic lookup assumed `order` indices grew with canonical
  // position; Conversion breaks that (Source Files sits before AIFs in the
  // visual order, but trails them in canonical STAGE_ORDER), so we use
  // indexOf instead. When requestedStage was filtered out (e.g. Extraction
  // on a Conversion period), fall back to walking canonical backward for the
  // closest earlier stage that survived the filter and anchor there.
  let startIdx = order.indexOf(requestedStage);
  if (startIdx < 0) {
    const requestedCanonicalIdx = canonical.indexOf(requestedStage);
    for (let ci = requestedCanonicalIdx - 1; ci >= 0; ci -= 1) {
      const idx = order.indexOf(canonical[ci]);
      if (idx >= 0) {
        startIdx = idx;
        break;
      }
    }
  }
  if (startIdx < 0) return fallback;

  for (let i = startIdx; i >= 0; i -= 1) {
    const stage = order[i];
    if (!isStageReachable(stage, destPeriod, project)) continue;
    const path = buildStagePath(
      stage,
      workflowId,
      newPeriodId,
      destPeriod,
      project,
      destJurisdictions
    );
    if (path) return path;
  }

  return fallback;
};

// ── Carrying the open work across a period switch ────────────────────────────

// The stages whose address names a file and a sheet: the extraction tab and the
// lookup tab, which share the classic page's query. The other two stages that
// open on one thing name it differently — Transformation by `extractId`, Output
// Data by `outputTable` — so each is carried by its own helper below.
const STAGES_CARRYING_SHEET = [WORKFLOW_STAGE_KEYS.EXTRACTION, WORKFLOW_STAGE_KEYS.LOOKUP_TABLES];

export const stageCarriesSheet = stage => STAGES_CARRYING_SHEET.indexOf(stage) >= 0;

const sameName = (left, right) =>
  String(left ?? '')
    .trim()
    .toLowerCase() ===
  String(right ?? '')
    .trim()
    .toLowerCase();

/**
 * The same file and sheet in the neighbouring period, as the period itself
 * recorded them.
 *
 * Source Files is where a new period's workbooks are paired with the previous
 * period's — that is the whole point of the step, and the pairing is saved.
 * So the crossing is not a guess at all where the two periods are neighbours:
 * `automation-fund-mappings` for the later of them holds every file pair, and
 * inside each, its sheet pairs.
 *
 * `forward` says which way we are reading the pairs: leaving the earlier period
 * for the later one, the file we hold is the pair's `previous_period_file`;
 * going back, it is its `current_period_file`.
 */
const mappedFilePairs = mappings => {
  const pairs = [];
  for (const fund of mappings?.mapped_input_funds ?? []) {
    const lists = [
      ...(fund?.mapped_bulk_upload_files?.mapped_list ?? []),
      ...(fund?.mapped_per_input_files ?? [])
    ];
    for (const pair of lists) {
      if (pair?.previous_period_file?.id && pair?.current_period_file?.id) pairs.push(pair);
    }
  }
  return pairs;
};

export const mapFileSheetByMappings = (mappings, fileId, sheetId, forward) => {
  if (!fileId) return null;

  const fromFileKey = forward ? 'previous_period_file' : 'current_period_file';
  const toFileKey = forward ? 'current_period_file' : 'previous_period_file';
  const fromSheetKey = forward ? 'previous_period_sheet' : 'current_period_sheet';
  const toSheetKey = forward ? 'current_period_sheet' : 'previous_period_sheet';

  const pair = mappedFilePairs(mappings).find(item => item?.[fromFileKey]?.id === fileId);
  if (!pair?.[toFileKey]?.id) return null;

  const sheetPair = sheetId
    ? (pair?.sheet_mappings?.mapped_list ?? []).find(item => item?.[fromSheetKey]?.id === sheetId)
    : undefined;

  return { fileId: pair[toFileKey].id, sheetId: sheetPair?.[toSheetKey]?.id };
};

/**
 * The workbook over there that holds this sheet.
 *
 * The file name is the weaker of the two: clients name the workbook after the
 * period it covers as often as not — `… - Period 1.xlsx` against `… - Period
 * 2.xlsx` — while the tabs inside keep their names, because those are what the
 * report is made of. So the name of the file is tried first and the name of the
 * sheet answers when it fails.
 *
 * Only when one file over there has that sheet. Two workbooks with a tab of the
 * same name is a question this cannot answer, and guessing between them would
 * open the wrong quarter's data under the right title.
 */
const findDestinationFile = (toFiles, fileName, sheetName) => {
  const byFileName = (toFiles ?? []).find(file => sameName(file?.name, fileName));
  if (byFileName) return byFileName;
  if (!sheetName) return undefined;

  const holding = (toFiles ?? []).filter(file =>
    (file?.sheets ?? []).some(sheet => sameName(sheet?.name, sheetName))
  );
  return holding.length === 1 ? holding[0] : undefined;
};

/**
 * The same workbook and sheet, in another period.
 *
 * Ids are per-period rows: Q2's copy of the same spreadsheet is a different
 * file id, and its sheets are different sheet ids again. What survives is what
 * the client named — the workbook they upload each quarter and the tabs inside
 * it — so the match is by name, which is the only thing the two periods share.
 *
 * A file that matches while its sheet does not still returns the file: landing
 * in the right workbook on its first sheet is nearer than landing nowhere, and
 * the page selects that first sheet on its own.
 */
export const mapFileSheetAcrossPeriods = (fromFiles, toFiles, fileId, sheetId) => {
  if (!fileId) return null;
  const fromFile = (fromFiles ?? []).find(file => file?.id === fileId);
  if (!fromFile) return null;

  const fromSheet = (fromFile.sheets ?? []).find(sheet => sheet?.id === sheetId);
  const toFile = findDestinationFile(toFiles, fromFile?.name, fromSheet?.name);
  if (!toFile?.id) return null;

  const toSheet = fromSheet
    ? (toFile.sheets ?? []).find(sheet => sameName(sheet?.name, fromSheet?.name))
    : undefined;

  return { fileId: toFile.id, sheetId: toSheet?.id };
};

/**
 * Where an extraction sits in the tree the source picker lists: which file,
 * which sheet, under which category. The ids are what the period's own file and
 * sheet mapping is keyed by, so this is the question to ask before crossing.
 */
export const findExtractLocation = (files, extractId) => {
  if (!extractId) return null;
  for (const file of files ?? []) {
    for (const sheet of file?.sheets ?? []) {
      const extraction = (sheet?.extractions ?? []).find(item => item?.id === extractId);
      if (extraction) {
        return {
          fileId: file?.id,
          sheetId: sheet?.id,
          file: file?.name,
          sheet: sheet?.name,
          category: extraction?.category
        };
      }
    }
  }
  return null;
};

/**
 * The same extraction, in another period.
 *
 * What the reader is following here is the category. Transformation lists its
 * sources as file / sheet with the category beside them, and the category is
 * the one of the three that means the same thing in both quarters: the sheet it
 * was taken from can be renamed or moved to another workbook, and Shareholder
 * data stays Shareholder data. So the category is what must match, and the file
 * is what decides when more than one extraction answers to it — the paired one
 * first, then the one by name, and a single candidate needs neither.
 *
 * Nothing in that category over there means no crossing. Landing on the
 * period's first extraction instead would be arriving at a different subject
 * with no sign that it changed.
 */
export const mapExtractAcrossPeriods = (fromFiles, toFiles, extractId, mapped) => {
  const from = findExtractLocation(fromFiles, extractId);
  if (!from) return null;

  const candidates = [];
  for (const file of toFiles ?? []) {
    for (const sheet of file?.sheets ?? []) {
      for (const extraction of sheet?.extractions ?? []) {
        if (extraction?.id && sameName(extraction?.category, from.category)) {
          candidates.push({ file, sheet, extraction });
        }
      }
    }
  }
  if (!candidates.length) return null;

  const onPairedSheet = mapped?.sheetId
    ? candidates.find(item => item.sheet?.id === mapped.sheetId)
    : undefined;
  const inPairedFile = mapped?.fileId
    ? candidates.find(item => item.file?.id === mapped.fileId)
    : undefined;
  const inNamedFile = candidates.find(item => sameName(item.file?.name, from.file));

  const match =
    onPairedSheet ??
    inPairedFile ??
    inNamedFile ??
    (candidates.length === 1 ? candidates[0] : null);

  return match ? { extractId: match.extraction.id } : null;
};

/** Adds the mapped file/sheet to a stage path, keeping whatever it already carries (`?tab=`). */
export const withFileSheet = (path, mapped) => {
  if (!path || !mapped?.fileId) return path;
  return withQuery(path, { fileId: mapped.fileId, sheetId: mapped.sheetId });
};

/** Adds `entries` to a stage path, keeping whatever it already carries (`?tab=`). Empty values are
    left off rather than written blank, so a missing one falls to the page's own default. */
export const withQuery = (path, entries) => {
  const pairs = Object.entries(entries ?? {}).filter(([, value]) => !!value);
  if (!path || !pairs.length) return path;
  const [base, query = ''] = path.split('?');
  const params = new URLSearchParams(query);
  for (const [key, value] of pairs) params.set(key, value);
  return `${base}?${params.toString()}`;
};
